import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { gamificationService } from '../services';

export const GamificationContext = createContext();

export const GamificationProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [recentAchievements, setRecentAchievements] = useState([]);
  const [xpGained, setXpGained] = useState({ amount: 0, visible: false });
  const [availableBadges, setAvailableBadges] = useState([]);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [toastData, setToastData] = useState(null);
  const [simpleToast, setSimpleToast] = useState({ visible: false, message: '', icon: null });

  // Fetch available badges when component mounts
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const response = await gamificationService.getBadges();
        setAvailableBadges(response.data.badges);
      } catch (error) {
        console.error('Error fetching badges:', error);
      }
    };

    fetchBadges();
  }, []);
  
  // Reset xpGained state after it's no longer visible (component will handle this)
  useEffect(() => {
    if (xpGained.visible) {
      const resetTimer = setTimeout(() => {
        setXpGained(prev => ({ ...prev, visible: false }));
      }, 5000);
      
      return () => clearTimeout(resetTimer);
    }
  }, [xpGained.visible]);

  // Get user's earned badges
  const getUserBadges = () => {
    if (!user?.badges || !user.badges.length) return [];
    
    // Group badges by ID and keep only the highest tier for each badge
    const badgeMap = {};
    user.badges.forEach(badge => {
      if (!badgeMap[badge.id] || badge.tier > badgeMap[badge.id].tier) {
        badgeMap[badge.id] = badge;
      }
    });
    
    // Convert back to array
    return Object.values(badgeMap);
  };

  // Process a gamification event
  const processEvent = async (action, metadata = {}) => {
    if (!user?.id) return null;
    
    try {
      // Check if action is an object (with userId, action, metadata)
      // or a string (just the action type)
      let userId = user.id;
      let actionType = action;
      let actionMetadata = metadata;
      
      if (typeof action === 'object' && action !== null) {
        // If action is an object, extract fields
        userId = action.userId || user.id;
        actionType = action.action;
        actionMetadata = action.metadata || {};
      }
      
      // Validate that we have a proper action type
      if (typeof actionType !== 'string') {
        console.error('Invalid action type:', actionType);
        return null;
      }
      
      const response = await gamificationService.processEvent({
        userId,
        action: actionType, 
        metadata: actionMetadata
      });
      
      const { xpGained: xpAmount, newBadges, levelUp } = response.data;
      
      // Display XP gain notification
      if (xpAmount > 0) {
        setXpGained({ amount: xpAmount, visible: true });
      }
      
      // Display achievement notification for new badges
      if (newBadges && newBadges.length > 0) {
        setRecentAchievements(newBadges);
        
        // Filter out duplicate badge types, keeping only the highest tier
        const uniqueBadges = {};
        newBadges.forEach(badge => {
          const existingBadge = uniqueBadges[badge.id];
          if (!existingBadge || badge.tier > existingBadge.tier) {
            uniqueBadges[badge.id] = badge;
          }
        });
        
        // Show toasts for each unique badge
        Object.values(uniqueBadges).forEach((badge, index) => {
          // Delay each toast by 2 seconds to avoid overwhelming the user
          setTimeout(() => {
            showAchievementToast(badge);
          }, index * 2000);
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error processing gamification event:', error);
      return null;
    }
  };
  
  // Display achievement toast notification
  const showAchievementToast = (achievement) => {
    // Find the badge details in availableBadges
    const badgeDetails = availableBadges.find(badge => badge.id === achievement.id);
    
    if (badgeDetails) {
      setToastData({
        ...achievement,
        name: badgeDetails.name,
        description: badgeDetails.description,
        tierDetails: badgeDetails.tiers.find(t => t.tier === achievement.tier)
      });
      
      setIsToastVisible(true);
      
      // Let the component handle its own animation timing
      // This timeout should be longer than the component's internal animation duration
      setTimeout(() => {
        setIsToastVisible(false);
      }, 8000);
    }
  };
  
  // Get color class for streak flame based on streak length
  const getStreakColorClass = (streakLength) => {
    if (!streakLength) return 'text-gray-400';
    
    if (streakLength >= 100) return 'text-purple-500';
    if (streakLength >= 30) return 'text-red-600';
    if (streakLength >= 14) return 'text-red-500';
    if (streakLength >= 7) return 'text-orange-500';
    return 'text-orange-400';
  };
  
  // Calculate progress to next level as percentage
  const calculateLevelProgress = (xp, currentLevel) => {
    if (!xp) return 0;
    
    const nextLevelXp = Math.pow((currentLevel ), 4); // XP needed for next level
    const currentLevelXp = Math.pow((currentLevel - 1), 4); // XP needed for current level
    
    const levelRange = nextLevelXp - currentLevelXp;
    const progress = xp - currentLevelXp;

    return Math.min(100, Math.max(0, (progress / levelRange) * 100));
  };

  // Show a simple toast notification
  const showSimpleToast = (opts, icon = null) => {
    if (typeof opts === 'string') {
      setSimpleToast({ visible: true, title: opts, subtitle: '', icon });
    } else {
      setSimpleToast({ visible: true, ...opts });
    }
    setTimeout(() => setSimpleToast({ visible: false, title: '', subtitle: '', icon: null }), 8000);
  };
  const hideSimpleToast = () => setSimpleToast({ visible: false, message: '', icon: null });

  // Value object with all context data and functions
  const contextValue = {
    xpGained,
    recentAchievements,
    availableBadges,
    isToastVisible,
    toastData,
    getUserBadges,
    processEvent,
    showAchievementToast,
    getStreakColorClass,
    calculateLevelProgress,
    hideToast: () => setIsToastVisible(false),
    showSimpleToast,
    hideSimpleToast,
    simpleToast
  };

  return (
    <GamificationContext.Provider value={contextValue}>
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => useContext(GamificationContext); 